// Import Subscribers modal — fetches candidates from a source, lets the
// operator pick which to accept, edit the proposed name, and either
// create-new or merge-into-existing.
//
// Phase 1: 'commio' source only. Candidates are unlinked locations with
// a label; accepting creates a new subscriber and links the location to
// it. Fuzzy dedup hints appear inline when the label matches an existing
// subscriber closely enough.

import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';

export default function ImportSubscribersModal({ source, agentId, onClose }) {
  const queryClient = useQueryClient();
  const [selection, setSelection] = useState({});  // source_ref → { checked, name, merge_into? }
  const [importToast, setImportToast] = useState(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['import-candidates', source, agentId || null],
    queryFn: () => api.get('/subscribers/import/candidates', { source, ...(agentId ? { agentId } : {}) }),
  });

  // Wrap in useMemo so the useEffect below has a stable dep — otherwise
  // we'd re-seed selection on every render as the identity changes.
  const candidates = useMemo(() => data?.candidates || [], [data?.candidates]);

  // Seed selection state as candidates arrive: default-check every one,
  // pre-fill the display-name field with the proposed value.
  useEffect(() => {
    if (!candidates.length) return;
    setSelection((prev) => {
      const next = { ...prev };
      for (const c of candidates) {
        if (!next[c.source_ref]) {
          next[c.source_ref] = {
            checked: true,
            name: c.display_name,
            merge_into: null,
          };
        }
      }
      return next;
    });
  }, [candidates]);

  const importMutation = useMutation({
    mutationFn: (accept) => api.post('/subscribers/import', { accept }, undefined, { timeoutMs: 60_000 }),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['subscribers'] });
      queryClient.invalidateQueries({ queryKey: ['locations'] });
      queryClient.invalidateQueries({ queryKey: ['import-candidates', source] });
      const created = res?.results?.filter((r) => r.ok && r.action === 'created').length || 0;
      const merged  = res?.results?.filter((r) => r.ok && r.action === 'merged').length  || 0;
      const failed  = res?.results?.filter((r) => !r.ok).length || 0;
      setImportToast({
        ok: failed === 0,
        message:
          (created ? `${created} created` : '') +
          (created && merged ? ', ' : '') +
          (merged ? `${merged} merged` : '') +
          (failed ? ` (${failed} failed)` : ''),
        detail: res?.results?.filter((r) => !r.ok).map((r) => r.error).slice(0, 3).join('; '),
      });
    },
  });

  const checkedRows = candidates.filter((c) => selection[c.source_ref]?.checked);

  function submit() {
    const accept = checkedRows.map((c) => {
      const s = selection[c.source_ref];
      return {
        source: c.source,
        source_ref: c.source_ref,
        display_name: (s.name || c.display_name).trim(),
        merge_into_subscriber_id: s.merge_into || undefined,
        hints: c.hints,
      };
    });
    importMutation.mutate(accept);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-3xl overflow-hidden rounded-lg bg-white shadow-xl">
        <div className="border-b border-gray-200 px-6 py-4">
          <h3 className="text-lg font-bold text-gray-900">
            Import subscribers from {sourceLabel(source)}
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            Review candidates discovered from an external system. Accept to
            create new subscribers (or merge into existing ones when a
            fuzzy match is flagged).
          </p>
        </div>

        <div className="max-h-[60vh] overflow-y-auto px-6 py-4">
          {isLoading && <p className="text-sm text-gray-500">Loading candidates…</p>}
          {error && (
            <div className="rounded-md border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
              {error?.body?.error || error.message}
            </div>
          )}
          {!isLoading && !error && candidates.length === 0 && (
            <p className="text-sm text-gray-500">
              Nothing to import — every {sourceLabel(source)} record already
              lines up with a Verso subscriber (or the source is empty).
            </p>
          )}
          {candidates.length > 0 && (
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-gray-500">
                <tr>
                  <th className="pb-2"></th>
                  <th className="pb-2">Proposed name</th>
                  <th className="pb-2">Address</th>
                  <th className="pb-2">Dedup hint</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {candidates.map((c) => {
                  const sel = selection[c.source_ref] || {};
                  return (
                    <tr key={`${c.source}:${c.source_ref}`} className="align-top">
                      <td className="py-2 pr-3">
                        <input
                          type="checkbox"
                          checked={sel.checked || false}
                          onChange={(e) => setSelection((prev) => ({
                            ...prev,
                            [c.source_ref]: { ...prev[c.source_ref], checked: e.target.checked },
                          }))}
                          className="rounded border-gray-300"
                        />
                      </td>
                      <td className="py-2 pr-3">
                        <input
                          type="text"
                          value={sel.name ?? c.display_name}
                          onChange={(e) => setSelection((prev) => ({
                            ...prev,
                            [c.source_ref]: { ...prev[c.source_ref], name: e.target.value },
                          }))}
                          disabled={!sel.checked || Boolean(sel.merge_into)}
                          className="w-full rounded border border-gray-300 px-2 py-1 text-sm disabled:bg-gray-50 disabled:text-gray-400"
                        />
                      </td>
                      <td className="py-2 pr-3 text-xs text-gray-600">
                        {[c.hints?.address, c.hints?.city, c.hints?.state]
                          .filter(Boolean).join(', ') || <span className="text-gray-300">—</span>}
                      </td>
                      <td className="py-2 pr-3 text-xs">
                        {c.dedup_target ? (
                          <label className="flex items-center gap-2 text-amber-800">
                            <input
                              type="checkbox"
                              checked={Boolean(sel.merge_into)}
                              onChange={(e) => setSelection((prev) => ({
                                ...prev,
                                [c.source_ref]: {
                                  ...prev[c.source_ref],
                                  merge_into: e.target.checked ? c.dedup_target.subscriber_id : null,
                                },
                              }))}
                              className="rounded border-amber-300"
                            />
                            Merge into &ldquo;{c.dedup_target.display_name}&rdquo;
                          </label>
                        ) : (
                          <span className="text-gray-300">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {importToast && (
          <div
            className={`border-t px-6 py-3 text-sm ${
              importToast.ok
                ? 'border-green-200 bg-green-50 text-green-700'
                : 'border-amber-200 bg-amber-50 text-amber-800'
            }`}
          >
            <div className="font-medium">{importToast.message}</div>
            {importToast.detail && (
              <div className="mt-1 text-xs opacity-75">{importToast.detail}</div>
            )}
          </div>
        )}

        <div className="flex justify-between gap-3 border-t border-gray-200 bg-gray-50 px-6 py-3">
          <div className="text-sm text-gray-500 self-center">
            {checkedRows.length} of {candidates.length} selected
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              disabled={importMutation.isPending}
              className="rounded-md border border-gray-300 px-4 py-1.5 text-sm font-medium text-gray-700 hover:bg-white disabled:opacity-50"
            >
              Close
            </button>
            <button
              onClick={submit}
              disabled={importMutation.isPending || checkedRows.length === 0}
              className="rounded-md bg-blue-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {importMutation.isPending ? 'Importing…' : `Import ${checkedRows.length}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function sourceLabel(source) {
  switch (source) {
    case 'commio': return 'Commio locations';
    case 'gdms':   return 'GDMS SIP accounts';
    case 'agent':  return 'PBX agent extensions';
    default:       return source;
  }
}
